import { useEffect, useMemo, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { UserAvatar } from "@/components/ui/user-avatar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { useTournamentPlayerUpdate } from "@/graphql/hooks/useTournamentPlayerUpdate"
import { useUserSearch } from "@/graphql/hooks/useUserSearch"
import { useToast } from "@/hooks/use-toast"
import { Trash2, User2, Lock, Search } from "lucide-react"
import { useCountries, useRegions } from "@/graphql/hooks/Locations/useLocations"
import { Badge } from "./ui/badge"
import { CountryFlag } from "./ui/country-flag"

interface TournamentPlayer {
  id: number
  byes: number
  drop: number
  raceId: number
  personId: number
  Person: {
    id: number
    fullName: string
    code: string
    email?: string
    avatar?: string
    urlAvatar?: string
    hasDocument?: boolean
    documentNumber?: string
    documentNumberType?: number
    Address: {
      countryId: number
      regionId: number
    }
  }
}

interface Tournament {
  statusId: number
  Game: {
    RaceGames: Array<{
      raceId: number
      sortOrder: number
      Race: {
        name: string
      }
    }>
  }
}

interface PlayerManagementTableProps {
  players: TournamentPlayer[]
  tournament: Tournament
  isAdmin?: boolean
  onPlayerUpdate: () => void
}

export const PlayerManagementTable = ({
  players,
  tournament,
  onPlayerUpdate,
  isAdmin = false,
}: PlayerManagementTableProps) => {
  const PLAYERS_PER_PAGE = 50
  const [deletePlayerId, setDeletePlayerId] = useState<number | null>(null)
  const [replacePlayerId, setReplacePlayerId] = useState<number | null>(null)
  const [listSearchInput, setListSearchInput] = useState("")
  const [listSearchTerm, setListSearchTerm] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedReplacement, setSelectedReplacement] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)

  const { updateTournamentPlayer, loading: updateLoading } = useTournamentPlayerUpdate()
  const { searchForUsers, searchResults, loading: searchLoading } = useUserSearch()

  const { countriesMap } = useCountries()
  const { regionsMap } = useRegions()

  const { toast } = useToast()

  const sortedPlayers = [...players].sort((a, b) =>
    a.Person.fullName.localeCompare(b.Person.fullName),
  )

  const filteredPlayers = useMemo(() => {
    const normalizedTerm = listSearchTerm.trim().toLowerCase()
    if (!normalizedTerm) return sortedPlayers

    return sortedPlayers.filter((player) => {
      const searchableValues = [
        player.Person.fullName,
        player.Person.code,
        player.Person.email || "",
        player.Person.documentNumber || "",
      ]

      return searchableValues.some((value) => value.toLowerCase().includes(normalizedTerm))
    })
  }, [listSearchTerm, sortedPlayers])

  const totalPages = Math.max(1, Math.ceil(filteredPlayers.length / PLAYERS_PER_PAGE))
  const pageStart = (currentPage - 1) * PLAYERS_PER_PAGE
  const paginatedPlayers = filteredPlayers.slice(pageStart, pageStart + PLAYERS_PER_PAGE)
  const desktopPageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1)
    }

    const start = Math.max(1, currentPage - 3)
    const end = Math.min(totalPages, start + 6)
    const adjustedStart = Math.max(1, end - 6)

    return Array.from({ length: end - adjustedStart + 1 }, (_, idx) => adjustedStart + idx)
  }, [currentPage, totalPages])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setListSearchTerm(listSearchInput)
    }, 400)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [listSearchInput])

  useEffect(() => {
    setCurrentPage(1)
  }, [listSearchTerm])

  const races = useMemo(() => {
    const razas = tournament.Game?.RaceGames || []
    return [...razas].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [tournament.Game.RaceGames])

  const handleRaceChange = async (player: TournamentPlayer, raceId: number) => {
    try {
      const race = races.find((x) => x.raceId === raceId)
      await updateTournamentPlayer({ id: player.id, action: "race", raceId })
      toast({
        title: "Raza Actualizada",
        description: `Raza "${race.Race.name}" fue asignada al jugador "${player.Person.fullName}" - ${player.Person.code}`,
        variant: "success",
      })
      onPlayerUpdate()
    } catch {
      toast({
        title: "Error al actualizar raza",
        description: "No se pudo actualizar la raza del jugador",
        variant: "destructive",
      })
    }
  }

  const handleDeletePlayer = async () => {
    if (!deletePlayerId) return
    try {
      await updateTournamentPlayer({ id: deletePlayerId, action: "delete" })
      toast({ title: "Jugador eliminado correctamente", variant: "success" })
      setDeletePlayerId(null)
      onPlayerUpdate()
    } catch {
      toast({
        title: "Error al eliminar jugador",
        description: "No se pudo eliminar el jugador del torneo",
        variant: "destructive",
      })
    }
  }

  const handleReplacePlayer = async () => {
    if (!replacePlayerId || !selectedReplacement) return
    try {
      await updateTournamentPlayer({
        id: replacePlayerId,
        action: "change",
        personId: selectedReplacement.id,
      })
      toast({ title: "Jugador reemplazado correctamente", variant: "success" })
      setReplacePlayerId(null)
      setSelectedReplacement(null)
      setSearchTerm("")
      onPlayerUpdate()
    } catch {
      toast({
        title: "Error al reemplazar jugador",
        description: "No se pudo reemplazar el jugador",
        variant: "destructive",
      })
    }
  }

  const searchPlayers = async () => {
    if (!searchTerm.trim()) return
    const excludedIds = players.map((p) => p.personId)
    await searchForUsers({
      search: searchTerm,
      excludes: excludedIds,
      tournamentId: 0,
      organizer: false,
    })
  }

  return (
    <>
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={listSearchInput}
            onChange={(e) => setListSearchInput(e.target.value)}
            placeholder="Buscar jugador por nombre, código, email o documento..."
            className="pl-9"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block">
        <Table className="dark:bg-gray-800">
          <TableHeader>
            <TableRow className="bg-gray-100 dark:bg-gray-700">
              <TableHead className="text-gray-900 dark:text-gray-100">#</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100">Jugador</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100">Código</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100">Dirección</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100">Raza</TableHead>
              <TableHead className="text-gray-900 dark:text-gray-100">Estado</TableHead>
              {![5, 6].includes(tournament.statusId) && (
                <TableHead className="text-gray-900 dark:text-gray-100">Acciones</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedPlayers.map((player, index) => (
              <TableRow
                key={player.id}
                className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <TableCell className="text-gray-900 dark:text-gray-100">
                  {pageStart + index + 1}
                </TableCell>
                <TableCell className="flex items-center gap-3 py-2 px-0 text-gray-900 dark:text-gray-100">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      src={player.Person.urlAvatar || player.Person.avatar}
                      name={player.Person.fullName}
                      size="md"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">{player.Person.fullName}</span>
                    {!player.Person.hasDocument && (
                      <Badge
                        variant="info"
                        className="px-2 mt-1 text-xs text-white dark:bg-blue-700 dark:text-gray-100"
                      >
                        Documento no informado
                      </Badge>
                    )}
                    {isAdmin && player.Person.hasDocument && (
                      <span className="text-xs text-muted-foreground dark:text-gray-300">
                        {player.Person.documentNumber}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <code className="bg-muted px-2 py-1 rounded text-xs dark:bg-gray-700 dark:text-gray-100">
                    {player.Person.code}
                  </code>
                </TableCell>
                <TableCell className="text-gray-900 dark:text-gray-100 py-2">
                  <p>
                    <CountryFlag
                      countryCode={countriesMap.get(player.Person?.Address?.countryId)?.iso}
                    />
                    {regionsMap.get(player.Person?.Address?.regionId)?.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate dark:text-gray-400">
                    {player.Person.email || "N/A"}
                  </p>
                </TableCell>
                <TableCell className="py-2">
                  <Select
                    value={player.raceId?.toString()}
                    onValueChange={(value) => handleRaceChange(player, parseInt(value))}
                    disabled={
                      !!player.drop || [5, 6].includes(tournament.statusId) || !races.length
                    }
                  >
                    <SelectTrigger className="w-full dark:bg-gray-700 dark:text-gray-100">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-800 dark:text-gray-100">
                      {races.map((race) => (
                        <SelectItem key={race.raceId} value={race.raceId.toString()}>
                          {race.Race.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="py-2">
                  {player.drop === 1 ? (
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-red-600 text-white dark:bg-red-500 dark:text-gray-100">
                      Drop
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-green-600 text-white dark:bg-green-500 dark:text-gray-100">
                      Activo
                    </span>
                  )}
                </TableCell>
                {![5, 6].includes(tournament.statusId) && (
                  <TableCell className="py-2">
                    <div className="flex gap-2">
                      {!player.drop && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReplacePlayerId(player.id)}
                          className="h-8 w-8 p-0 border-gray-300 dark:border-gray-400 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <User2 className="h-4 w-4" />
                        </Button>
                      )}
                      {player.drop === 1 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          className="h-8 w-8 p-0 border-gray-300 dark:border-gray-400 text-gray-400 dark:text-gray-500"
                        >
                          <Lock className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeletePlayerId(player.id)}
                          className="h-8 w-8 p-0 bg-red-600 text-white dark:bg-red-500 dark:text-gray-100 hover:bg-red-700 dark:hover:bg-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-3">
        {paginatedPlayers.map((player) => (
          <Card
            key={player.id}
            className="bg-white dark:bg-gray-800 shadow-md rounded-xl overflow-hidden"
          >
            <CardContent className="p-3 sm:p-4">
              {/* --- Cabecera con avatar, nombre y estado --- */}
              <div className="flex items-start gap-3 sm:gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                    <UserAvatar
                      src={player.Person.urlAvatar || player.Person.avatar}
                      name={player.Person.fullName}
                      size="lg"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Información del jugador */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm sm:text-xs font-medium">
                      {player.Person.fullName}
                    </Badge>

                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        player.drop === 1
                          ? "bg-red-600 text-white dark:bg-red-500"
                          : "bg-green-600 text-white dark:bg-green-500"
                      }`}
                    >
                      {player.drop === 1 ? "Drop" : "Activo"}
                    </span>
                  </div>

                  <div className="mt-1 space-y-0.5">
                    <p className="text-xs text-muted-foreground dark:text-gray-300 truncate">
                      {player.Person.code}
                    </p>
                    {player.Person.email && (
                      <p className="text-xs text-muted-foreground dark:text-gray-300 truncate">
                        {player.Person.email}
                      </p>
                    )}
                    <div className="flex items-center gap-1 text-xs text-muted-foreground dark:text-gray-300">
                      <CountryFlag
                        countryCode={countriesMap.get(player.Person?.Address?.countryId)?.iso}
                      />
                      <span className="truncate">
                        {regionsMap.get(player.Person?.Address?.regionId)?.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* --- Línea única: Select + Botones --- */}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <Select
                  value={player.raceId?.toString()}
                  onValueChange={(value) => handleRaceChange(player, parseInt(value))}
                  disabled={!!player.drop || [5, 6].includes(tournament.statusId) || !races.length}
                >
                  <SelectTrigger className="flex-1 min-w-[120px] sm:w-36 h-9 text-sm">
                    <SelectValue placeholder="Raza" />
                  </SelectTrigger>
                  <SelectContent>
                    {races.map((race) => (
                      <SelectItem key={race.raceId} value={race.raceId.toString()}>
                        {race.Race.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {![5, 6].includes(tournament.statusId) && (
                  <div className="flex gap-2">
                    {!player.drop && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setReplacePlayerId(player.id)}
                        className="h-9 w-9 border-gray-300 dark:border-gray-400 text-gray-700 dark:text-gray-100"
                      >
                        <User2 className="h-4 w-4" />
                      </Button>
                    )}
                    {player.drop === 1 ? (
                      <Button
                        variant="outline"
                        size="icon"
                        disabled
                        className="h-9 w-9 border-gray-300 dark:border-gray-400 text-gray-400 dark:text-gray-500"
                      >
                        <Lock className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => setDeletePlayerId(player.id)}
                        className="h-9 w-9 bg-red-600 text-white dark:bg-red-500 dark:text-gray-100 hover:bg-red-700 dark:hover:bg-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPlayers.length > PLAYERS_PER_PAGE && (
        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            {pageStart + 1}-{Math.min(pageStart + PLAYERS_PER_PAGE, filteredPlayers.length)} de{" "}
            {filteredPlayers.length} jugadores
          </p>

          <div className="hidden sm:flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              Inicio
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            {desktopPageNumbers[0] > 1 && (
              <>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)}>
                  1
                </Button>
                <span className="text-sm text-muted-foreground">...</span>
              </>
            )}

            {desktopPageNumbers.map((pageNumber) => (
              <Button
                key={pageNumber}
                variant={currentPage === pageNumber ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </Button>
            ))}

            {desktopPageNumbers[desktopPageNumbers.length - 1] < totalPages && (
              <>
                <span className="text-sm text-muted-foreground">...</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)}>
                  {totalPages}
                </Button>
              </>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Final
            </Button>
          </div>

          <div className="flex sm:hidden items-center gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              {currentPage}/{totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletePlayerId} onOpenChange={() => setDeletePlayerId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar este jugador del torneo? Esta acción no se puede
              deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDeletePlayerId(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeletePlayer} disabled={updateLoading}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Player Dialog */}
      <Dialog
        open={!!replacePlayerId}
        onOpenChange={() => {
          setReplacePlayerId(null)
          setSelectedReplacement(null)
          setSearchTerm("")
        }}
      >
        <DialogContent className="max-w-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
          <DialogHeader>
            <DialogTitle>Reemplazar jugador</DialogTitle>
            <DialogDescription>
              Busca y selecciona un jugador para reemplazar al actual.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Buscar por código o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchPlayers()}
              />
              <Button onClick={searchPlayers} disabled={searchLoading} size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {searchResults.map((person) => (
                  <Card
                    key={person.id}
                    className={`cursor-pointer transition-colors border dark:border-gray-700 ${
                      selectedReplacement?.id === person.id
                        ? "bg-primary/10 dark:bg-primary/20"
                        : "hover:bg-muted dark:hover:bg-gray-800"
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="font-medium">{person.fullName}</div>
                      <div className="text-sm text-muted-foreground dark:text-gray-400">
                        {person.code} • {person.email}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setReplacePlayerId(null)
                setSelectedReplacement(null)
                setSearchTerm("")
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleReplacePlayer} disabled={!selectedReplacement || updateLoading}>
              Reemplazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
